import { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { CalendarPlus, ListChecks } from '@phosphor-icons/react';
import AppointmentCard from './AppointmentCard';
import CurrentTimeLine from './CurrentTimeLine';
import { useSelector } from 'react-redux';
import { selectClinicProfile } from '../../store/slices/authSlice';
import { buildCalendarHours, getClinicCalendarStartHour } from '../../utils/calendarHours';
import { useCalendarColorMode, matchesConfirmationFilter } from '../../utils/appointmentDisplay';
import { formatLocalDateString, HEBREW_DAYS_SHORT, HEBREW_DAYS_FULL } from '../../utils/dateUtils';
import { getHolidayForDate, getHolidaySlotSeverity } from '../../utils/israeliHolidays';
import { AbsenceBands, AbsenceHeaderBadge } from './AbsenceBands';
import './DayView.css';

const SLOT_HEIGHT = 86;
const LONG_PRESS_MS = 500;

// Compute per-appointment { col, totalCols } using CLUSTER-LOCAL totalCols.
//
// A "cluster" is a maximal run of appointments connected by overlap (possibly
// transitively — A overlaps B, B overlaps C ⇒ {A,B,C} is one cluster). Every
// appointment within a cluster shares the same `totalCols` (= max columns
// simultaneously used inside that cluster), but appointments in different
// clusters each get their own `totalCols`.
//
// Why: with a single day-wide `totalCols`, a brief 3-way overlap at 10:00
// would squish every unrelated appointment later in the day to 1/3 width.
// Clustering keeps lone / pair appointments at full (or 1/2) width.
function getOverlapLayout(appointments) {
  if (!appointments.length) return {};
  const sorted = [...appointments].sort((a, b) => {
    const aStart = new Date(a.startTime).getTime();
    const bStart = new Date(b.startTime).getTime();
    return aStart !== bStart ? aStart - bStart : new Date(b.endTime).getTime() - new Date(a.endTime).getTime();
  });

  const result = {};
  let clusterApts = [];     // [{ id, col }] — members of the in-flight cluster
  let clusterCols = [];     // per-column end times within the current cluster
  let clusterEnd = 0;       // max endTime seen in the current cluster

  const flushCluster = () => {
    const totalCols = clusterCols.length;
    clusterApts.forEach(({ id, col }) => {
      result[id] = { col, totalCols };
    });
    clusterApts = [];
    clusterCols = [];
    clusterEnd = 0;
  };

  sorted.forEach((apt) => {
    const aptStart = new Date(apt.startTime).getTime();
    const aptEnd = new Date(apt.endTime).getTime();

    // No overlap with any active apt in the cluster → flush & start a new one.
    if (clusterApts.length > 0 && aptStart >= clusterEnd) {
      flushCluster();
    }

    let placed = false;
    for (let col = 0; col < clusterCols.length; col++) {
      if (aptStart >= clusterCols[col]) {
        clusterCols[col] = aptEnd;
        clusterApts.push({ id: apt.id, col });
        placed = true;
        break;
      }
    }
    if (!placed) {
      clusterApts.push({ id: apt.id, col: clusterCols.length });
      clusterCols.push(aptEnd);
    }

    if (aptEnd > clusterEnd) clusterEnd = aptEnd;
  });

  if (clusterApts.length > 0) flushCluster();

  return result;
}

function DayView({
  date,
  appointments,
  availabilitySlots = [],
  roomAvailability = null,
  onSlotClick,
  onAppointmentClick,
  onAppointmentContextMenu,
  onAddTask,
  onDragDrop,
  showTherapistName = false,
  therapistColorMap = null,
  confirmationFilter = 'all',
  absences = [], // multi-clinic v2 (see WeekView)
}) {
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [draggedAptId, setDraggedAptId] = useState(null);
  const touchRef = useRef({ timer: null, ghost: null, isDragging: false, apt: null, card: null, startX: 0, startY: 0, indicator: null });

  // Clinic-configurable grid start (clinic.calendarStartHour, default 05:00) → rows up to 23:00
  const clinicProfile = useSelector(selectClinicProfile);
  const FIRST_HOUR = getClinicCalendarStartHour(clinicProfile);
  const HOURS = useMemo(() => buildCalendarHours(FIRST_HOUR), [FIRST_HOUR]);
  // Clinic color mode (status vs treatment type) — one subscription per view.
  const { mode: colorMode, treatmentTypeMap } = useCalendarColorMode();

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) closeContextMenu();
    };
    const handleKey = (e) => { if (e.key === 'Escape') closeContextMenu(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    document.addEventListener('scroll', closeContextMenu, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('scroll', closeContextMenu, true);
    };
  }, [contextMenu, closeContextMenu]);

  useLayoutEffect(() => {
    if (!contextMenu || !contextMenuRef.current) return;
    const rect = contextMenuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = contextMenu.x;
    let y = contextMenu.y;
    if (x + rect.width > vw - 8) x = vw - rect.width - 8;
    if (y + rect.height > vh - 8) y = vh - rect.height - 8;
    contextMenuRef.current.style.left = `${x}px`;
    contextMenuRef.current.style.top = `${y}px`;
  }, [contextMenu]);

  const handleContextMenu = (e, hour) => {
    if (e.target.closest('.appointment-card')) return;
    e.preventDefault();
    const slotDate = new Date(date);
    slotDate.setHours(hour, 0, 0, 0);
    setContextMenu({ x: e.clientX, y: e.clientY, slotDate, hour });
  };

  const handleMenuAction = (action) => {
    const { slotDate } = contextMenu || {};
    closeContextMenu();
    if (!slotDate) return;
    if (action === 'create-appointment') onSlotClick(slotDate);
    else if (action === 'add-task') onAddTask(date);
  };

  const dayOfWeek = date.getDay();
  const dateStr = formatLocalDateString(date);

  const getSlotTooltip = (hour) => {
    const dayName = HEBREW_DAYS_FULL[dayOfWeek];
    const dateFormatted = date.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
    const base = `יום ${dayName}, ${dateFormatted} | ${hour.toString().padStart(2, '0')}:00`;
    return isBlockedHour(hour) ? `${base} | זמן חסום` : base;
  };

  const dayAvailability = useMemo(() => {
    const daySlot = availabilitySlots.find((slot) => slot.date === dateStr);
    return daySlot?.slots || [];
  }, [availabilitySlots, dateStr]);

  const dayBlocked = useMemo(() => {
    const daySlot = availabilitySlots.find((slot) => slot.date === dateStr);
    return daySlot?.blocked || [];
  }, [availabilitySlots, dateStr]);

  const isAvailableHour = (hour) => {
    if (!dayAvailability.length) return false;
    return dayAvailability.some((slot) => {
      const startHour = parseInt(slot.start.split(':')[0], 10);
      const endHour = parseInt(slot.end.split(':')[0], 10);
      return hour >= startHour && hour < endHour;
    });
  };

  const roomDay = useMemo(() => {
    if (!roomAvailability) return null;
    return roomAvailability.find((d) => d.date === dateStr) || null;
  }, [roomAvailability, dateStr]);

  const getRoomHourState = (hour) => {
    if (!roomDay) return null;
    if (roomDay.mode === 'open') return 'open';
    if (roomDay.mode === 'closed' || roomDay.mode === 'holiday') return 'closed';
    const within = (roomDay.slots || []).some((slot) => {
      const sh = parseInt(slot.start.split(':')[0], 10);
      const eh = parseInt(slot.end.split(':')[0], 10);
      return hour >= sh && hour < eh;
    });
    return within ? 'open' : 'closed';
  };

  const isBlockedHour = (hour) => {
    if (!dayBlocked.length) return false;
    return dayBlocked.some((slot) => {
      const startHour = parseInt(slot.start.split(':')[0], 10);
      const endHour = parseInt(slot.end.split(':')[0], 10);
      return hour >= startHour && hour < endHour;
    });
  };

  const isPastSlot = (hour) => {
    const now = new Date();
    const slotDate = new Date(date);
    slotDate.setHours(hour + 1, 0, 0, 0);
    return slotDate < now;
  };

  const positionedAppointments = useMemo(() => {
    const layout = getOverlapLayout(appointments);
    const totalGridHeight = HOURS.length * SLOT_HEIGHT;
    return appointments.map(apt => {
      const start = new Date(apt.startTime);
      const end = new Date(apt.endTime);
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const firstMinute = FIRST_HOUR * 60;
      const topPx = Math.max(0, ((startMinutes - firstMinute) / 60) * SLOT_HEIGHT);
      const durationMinutes = (end - start) / (1000 * 60);
      const heightPx = Math.min(Math.max((durationMinutes / 60) * SLOT_HEIGHT, 24), totalGridHeight - topPx);
      const overlap = layout[apt.id] || { col: 0, totalCols: 1 };
      return { ...apt, topPx, heightPx, overlap };
    });
  }, [appointments, HOURS, FIRST_HOUR]);

  // Auto-scroll once per displayed day to just above the earliest meeting
  // (browser clamps to the grid end if the meeting is late in the day).
  const bodyRef = useRef(null);
  const autoScrolledKeyRef = useRef(null);
  useEffect(() => {
    const key = formatLocalDateString(date);
    if (autoScrolledKeyRef.current === key) return;
    if (!positionedAppointments.length) return;
    autoScrolledKeyRef.current = key;
    bodyRef.current?.scrollTo({
      top: Math.max(0, Math.min(...positionedAppointments.map((a) => a.topPx)) - SLOT_HEIGHT),
      behavior: "smooth",
    });
  }, [positionedAppointments, date]);

  const handleSlotClick = (hour) => {
    const slotDate = new Date(date);
    slotDate.setHours(hour, 0, 0, 0);
    onSlotClick(slotDate);
  };

  const formatHour = (hour) => `${hour.toString().padStart(2, '0')}:00`;

  const canDrag = (apt) => apt.status !== 'cancelled' && apt.status !== 'completed';

  // === HTML5 Drag & Drop (Desktop) ===
  const handleDragStart = (e, apt) => {
    if (!canDrag(apt)) { e.preventDefault(); return; }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', apt.id);
    setDraggedAptId(apt.id);
    requestAnimationFrame(() => {
      e.target.closest('.positioned-appointment')?.classList.add('is-dragging');
    });
  };

  const handleDragEnd = () => {
    setDraggedAptId(null);
    setDropIndicator(null);
    document.querySelectorAll('.is-dragging').forEach(el => el.classList.remove('is-dragging'));
  };

  const handleSlotDragOver = (e, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const isBottomHalf = y > rect.height / 2;
    setDropIndicator({ hour, isBottomHalf });
  };

  const handleSlotDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropIndicator(null);
  };

  const handleSlotDrop = (e, hour) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minute = y > rect.height / 2 ? 30 : 0;
    const apt = appointments.find(a => a.id === draggedAptId);
    if (apt && onDragDrop) onDragDrop(apt, date, hour, minute);
    handleDragEnd();
  };

  // === Touch Drag (Mobile) ===
  const handleTouchStart = useCallback((e, apt) => {
    if (!canDrag(apt)) return;
    const ref = touchRef.current;
    const touch = e.touches[0];
    ref.startX = touch.clientX;
    ref.startY = touch.clientY;
    ref.apt = apt;
    ref.card = e.currentTarget;

    ref.timer = setTimeout(() => {
      ref.isDragging = true;
      setDraggedAptId(apt.id);
      const card = ref.card;
      const ghost = card.cloneNode(true);
      ghost.className = 'drag-ghost';
      ghost.style.cssText = `position:fixed;width:${card.offsetWidth}px;pointer-events:none;z-index:10000;opacity:0.85;transform:scale(1.03);left:${touch.clientX - card.offsetWidth / 2}px;top:${touch.clientY - 20}px;border-radius:6px;box-shadow:0 8px 30px rgba(0,0,0,0.2);`;
      document.body.appendChild(ghost);
      ref.ghost = ghost;
      card.classList.add('is-dragging');
      if (navigator.vibrate) navigator.vibrate(50);
    }, LONG_PRESS_MS);
  }, []);

  const handleGlobalTouchMove = useCallback((e) => {
    const ref = touchRef.current;
    if (!ref.isDragging) {
      if (ref.timer) {
        const t = e.touches[0];
        if (Math.abs(t.clientX - ref.startX) > 10 || Math.abs(t.clientY - ref.startY) > 10) {
          clearTimeout(ref.timer);
          ref.timer = null;
        }
      }
      return;
    }
    e.preventDefault();
    const touch = e.touches[0];
    if (ref.ghost) {
      ref.ghost.style.left = `${touch.clientX - ref.ghost.offsetWidth / 2}px`;
      ref.ghost.style.top = `${touch.clientY - 20}px`;
      ref.ghost.style.display = 'none';
    }
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (ref.ghost) ref.ghost.style.display = '';
    const slot = el?.closest('.hour-slot');
    if (slot?.dataset.hour) {
      const rect = slot.getBoundingClientRect();
      const y = touch.clientY - rect.top;
      const isBottomHalf = y > rect.height / 2;
      const hour = parseInt(slot.dataset.hour, 10);
      ref.indicator = { hour, isBottomHalf };
      setDropIndicator({ hour, isBottomHalf });
    } else {
      ref.indicator = null;
      setDropIndicator(null);
    }
  }, []);

  const cleanupTouch = useCallback(() => {
    const ref = touchRef.current;
    clearTimeout(ref.timer);
    ref.timer = null;
    if (ref.isDragging) {
      const ind = ref.indicator;
      if (ind && ref.apt && onDragDrop) {
        onDragDrop(ref.apt, date, ind.hour, ind.isBottomHalf ? 30 : 0);
      }
      if (ref.ghost) { ref.ghost.remove(); ref.ghost = null; }
      if (ref.card) ref.card.classList.remove('is-dragging');
    }
    ref.isDragging = false;
    ref.apt = null;
    ref.card = null;
    ref.indicator = null;
    setDraggedAptId(null);
    setDropIndicator(null);
  }, [date, onDragDrop]);

  useEffect(() => {
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', cleanupTouch);
    document.addEventListener('touchcancel', cleanupTouch);
    return () => {
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', cleanupTouch);
      document.removeEventListener('touchcancel', cleanupTouch);
    };
  }, [handleGlobalTouchMove, cleanupTouch]);

  const dayHoliday = getHolidayForDate(date);

  return (
    <div className="day-view">
      <div className="day-view-header">
        <div className="day-header-cell">
          <span className="day-name">{HEBREW_DAYS_SHORT[dayOfWeek]}'</span>
          <span className="day-date">{date.getDate()}</span>
          {dayHoliday && (
            <span className={`day-holiday-badge ${dayHoliday.severity}`} title={dayHoliday.name}>
              {dayHoliday.name}
            </span>
          )}
          <AbsenceHeaderBadge absences={absences} dateStr={dateStr} showName={showTherapistName} />
        </div>
      </div>

      <div className="day-view-body" ref={bodyRef}>
        <div className="time-column">
          {HOURS.map((hour) => (
            <div key={hour} className="time-label">{formatHour(hour)}</div>
          ))}
        </div>

        <div className="day-column">
          {HOURS.map((hour) => {
            const isAvailable = isAvailableHour(hour);
            const isBlocked = isBlockedHour(hour);
            const isPast = isPastSlot(hour);
            const isDropTarget = dropIndicator?.hour === hour;
            const holidaySeverity = getHolidaySlotSeverity(dayHoliday, hour);
            const roomHourState = getRoomHourState(hour);
            return (
              <div
                key={hour}
                className={`hour-slot ${isAvailable ? 'available-slot' : ''} ${isBlocked ? 'blocked-slot' : ''} ${isPast ? 'past-slot' : ''} ${isDropTarget ? 'drop-target' : ''} ${holidaySeverity ? `holiday-slot-${holidaySeverity}` : ''} ${roomHourState ? `room-${roomHourState}-slot` : ''}`}
                data-hour={hour}
                onClick={() => !isPast && !draggedAptId && handleSlotClick(hour)}
                onContextMenu={(e) => !isPast && handleContextMenu(e, hour)}
                onDragOver={(e) => !isPast && handleSlotDragOver(e, hour)}
                onDragLeave={handleSlotDragLeave}
                onDrop={(e) => !isPast && handleSlotDrop(e, hour)}
                data-tooltip={!draggedAptId ? (isPast ? 'זמן זה כבר עבר' : getSlotTooltip(hour)) : undefined}
              >
                {isDropTarget && (
                  <div className={`drop-indicator ${dropIndicator.isBottomHalf ? 'bottom-half' : 'top-half'}`}>
                    <span className="drop-time-badge">
                      {`${hour.toString().padStart(2, '0')}:${dropIndicator.isBottomHalf ? '30' : '00'}`}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          <AbsenceBands
            absences={absences}
            dateStr={dateStr}
            firstHour={FIRST_HOUR}
            slotHeight={SLOT_HEIGHT}
            hoursCount={HOURS.length}
            showName={showTherapistName}
          />
          <div className="appointments-overlay">
            {positionedAppointments.map((apt) => (
              <div
                key={apt.id}
                className={`positioned-appointment ${draggedAptId === apt.id ? 'is-dragging' : ''}`}
                style={{
                  top: `${apt.topPx}px`,
                  height: `${apt.heightPx}px`,
                  ...(apt.overlap.totalCols > 1 ? {
                    left: `${(apt.overlap.col / apt.overlap.totalCols) * 100}%`,
                    right: 'auto',
                    width: `calc(${(100 / apt.overlap.totalCols).toFixed(2)}% - 2px)`,
                  } : {}),
                }}
                draggable={canDrag(apt)}
                onDragStart={(e) => handleDragStart(e, apt)}
                onDragEnd={handleDragEnd}
                onTouchStart={(e) => handleTouchStart(e, apt)}
              >
                <AppointmentCard
                  appointment={apt}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!draggedAptId) onAppointmentClick(apt);
                  }}
                  onContextMenu={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onAppointmentContextMenu?.(e, apt);
                  }}
                  compact={false}
                  slotHeight={SLOT_HEIGHT}
                  showTherapistName={showTherapistName}
                  therapistColor={therapistColorMap?.[apt.therapistId]?.color || null}
                  colorMode={colorMode}
                  treatmentTypeMap={treatmentTypeMap}
                  dimmed={!matchesConfirmationFilter(apt, confirmationFilter)}
                />
              </div>
            ))}
          </div>

          {date.toDateString() === new Date().toDateString() && (
            <CurrentTimeLine
              firstHour={FIRST_HOUR}
              lastHour={FIRST_HOUR + HOURS.length}
              slotHeight={SLOT_HEIGHT}
            />
          )}
        </div>
      </div>

      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="calendar-context-menu"
          style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x }}
        >
          <div className="context-menu-date-header">
            {date.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' })}
            {' • '}
            {contextMenu.hour.toString().padStart(2, '0')}:00
          </div>
          <button className="context-menu-item" onClick={() => handleMenuAction('create-appointment')}>
            <CalendarPlus size={18} weight="duotone" />
            <span>צור פגישה בשעה זו</span>
          </button>
          <div className="context-menu-divider" />
          <button className="context-menu-item" onClick={() => handleMenuAction('add-task')}>
            <ListChecks size={18} weight="duotone" />
            <span>הוסף משימה בתאריך זה</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default DayView;
